import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { activeMembership, useSession } from '../../lib/store';
import { Card, Empty, Notice, PageHeading, Spinner } from '../../components/Shell';
import { Stamp } from '../../components/Stamp';
import type { SitterSchedule } from '../../lib/types';

/** A bare 'YYYY-MM-DD' parses as UTC midnight, which shifts a day westward
    in a timezone behind UTC; anchoring at noon avoids that. */
function parseDateOnly(value: string): Date {
  return new Date(`${value}T12:00:00`);
}

function toDateOnly(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

/** Monday of the week containing `date`. */
function startOfWeek(date: Date): Date {
  const day = date.getDay(); // 0 Sun .. 6 Sat
  return addDays(date, -((day + 6) % 7));
}

const WEEKDAY_LABEL = new Intl.DateTimeFormat('en-US', { weekday: 'short' });
const DAY_LABEL = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });

/**
 * The week grid: sitters down one axis, days across, toggling who works.
 * AvailabilityDay still governs whether there is a bed; this only answers
 * whether there is a person, which is why "nobody on" is called out on the
 * date itself rather than folded into a capacity number.
 */
export function AdminSchedule() {
  const session = useSession();
  const tenantId = session.tenant?.tenantId;
  const membership = activeMembership(session);
  const isOwner = membership?.role === 'owner';
  const myUserId = session.user?.userId;

  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [schedule, setSchedule] = useState<SitterSchedule | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingKey, setSavingKey] = useState<string | null>(null);

  const dates = useMemo(
    () => Array.from({ length: 7 }, (_, i) => toDateOnly(addDays(weekStart, i))),
    [weekStart]
  );
  const from = dates[0];
  const to = dates[dates.length - 1];

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const result = await api<SitterSchedule>(`/tenants/${tenantId}/schedule?from=${from}&to=${to}`);
      setSchedule(result);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The schedule could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [tenantId, from, to]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggle = async (userId: string, date: string, currentlyWorking: boolean) => {
    if (!tenantId) return;
    const key = `${userId}#${date}`;
    setSavingKey(key);
    try {
      await api(`/tenants/${tenantId}/schedule/${userId}/${date}`, {
        method: 'PUT',
        body: { working: !currentlyWorking },
      });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The change could not be saved.');
    } finally {
      setSavingKey(null);
    }
  };

  const dayByDate = new Map((schedule?.days ?? []).map((day) => [day.date, day]));

  return (
    <div className="mx-auto max-w-6xl px-5 pb-24 pt-12">
      <PageHeading
        eyebrow={session.tenant?.businessName}
        title="Schedule"
        lead="Who is working which day. A date nobody is on can be declined for want of a sitter, whatever the bed count says."
        actions={
          <div className="flex flex-wrap gap-2">
            <button type="button" className="btn btn-secondary" onClick={() => setWeekStart((w) => addDays(w, -7))}>
              Previous week
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => setWeekStart(startOfWeek(new Date()))}>
              This week
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => setWeekStart((w) => addDays(w, 7))}>
              Next week
            </button>
          </div>
        }
      />

      {error && (
        <div className="mb-8">
          <Notice tone="error">{error}</Notice>
        </div>
      )}

      {loading && !schedule ? (
        <Spinner label="Loading the schedule" />
      ) : !schedule || schedule.sitters.length === 0 ? (
        <Empty title="No sitters yet" body="Invite staff from Settings before scheduling shifts." />
      ) : (
        <Card>
          {/* A week grid is wide: its own scroller keeps the page body from
              scrolling sideways. */}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] border-collapse text-sm">
              <thead>
                <tr>
                  <th className="min-w-[160px] border-b border-[color:var(--color-rule)] px-4 py-3 text-left font-semibold">
                    Sitter
                  </th>
                  {dates.map((date) => {
                    const day = dayByDate.get(date);
                    return (
                      <th
                        key={date}
                        className="min-w-[104px] border-b border-[color:var(--color-rule)] px-3 py-3 text-center font-semibold"
                      >
                        <div>{WEEKDAY_LABEL.format(parseDateOnly(date))}</div>
                        <div className="text-xs font-normal text-[color:var(--color-ink-soft)]">
                          {DAY_LABEL.format(parseDateOnly(date))}
                        </div>
                        {day && !day.anyoneWorking && (
                          <div className="mt-1.5">
                            <Stamp tone="flag">Nobody on</Stamp>
                          </div>
                        )}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {schedule.sitters.map((sitter) => (
                  <tr key={sitter.userId} className="border-b border-[color:var(--color-rule)] last:border-b-0">
                    <td className="min-w-0 px-4 py-3">
                      <p className="min-w-0 truncate font-semibold">{sitter.name ?? sitter.userId}</p>
                      <p className="text-xs text-[color:var(--color-ink-soft)]">
                        {sitter.role === 'owner' ? 'Owner' : 'Staff'}
                      </p>
                    </td>
                    {dates.map((date) => {
                      const entry = dayByDate.get(date)?.sitters.find((s) => s.userId === sitter.userId);
                      const working = entry?.working ?? false;
                      const key = `${sitter.userId}#${date}`;
                      const canEdit = isOwner || sitter.userId === myUserId;
                      return (
                        <td key={date} className="min-w-0 px-3 py-3 text-center">
                          <button
                            type="button"
                            className={`btn w-full ${working ? 'btn-primary' : 'btn-secondary'}`}
                            disabled={!canEdit || savingKey === key}
                            title={canEdit ? undefined : 'Only this sitter or an owner can change this'}
                            onClick={() => void toggle(sitter.userId, date, working)}
                          >
                            {savingKey === key ? 'Saving' : working ? 'Working' : 'Off'}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
